/**
 * Output alerts gathered over every trace on the log. Each message is raised
 * once, prefixed with the D.No. of every trace that raised it, so with several
 * traces the reader can tell which one an alert belongs to.
 */
export function createTraceAlerts() {
  const byMessage = new Map();
  return {
    /** Record `message` for the trace at `index` (0-based). */
    add(message, index) {
      if (!message) return;
      if (!byMessage.has(message)) byMessage.set(message, new Set());
      byMessage.get(message).add(index + 1);
    },
    /**
     * "D.No. 1, 2: message" for each distinct message, in first-raised order.
     * With `only` (a set of 0-based trace indexes), just those traces count.
     */
    messages(only) {
      return [...byMessage].flatMap(([message, numbers]) => {
        const shown = only ? [...numbers].filter((n) => only.has(n - 1)) : [...numbers];
        return shown.length ? [`${dNoPrefix(shown)} ${message}`] : [];
      });
    },
  };
}

// Each tunnel's traces as last plotted. Kept outside the output component,
// which unmounts whenever the user goes back to the input form.
const lastPlotted = new Map();

/**
 * Indexes of the traces that are new or edited since `tunnel` was last
 * plotted. Only their alerts pop up; every alert stays printed on the output.
 */
export function changedTraces(tunnel, dimensions) {
  const keys = dimensions.map((d) => JSON.stringify(d));
  const before = new Map();
  (lastPlotted.get(tunnel) || []).forEach((k) => before.set(k, (before.get(k) || 0) + 1));
  lastPlotted.set(tunnel, keys);
  const changed = new Set();
  keys.forEach((k, i) => {
    if (before.get(k)) before.set(k, before.get(k) - 1);
    else changed.add(i);
  });
  return changed;
}

export const dNoPrefix = (numbers) =>
  `D.No. ${[...numbers].sort((a, b) => a - b).join(", ")}:`;

/** Raised for each trace that runs past either end of the log. */
export const CLIP_ALERT = "The trace extends beyond the log and is clipped at the log limit";

const tenths = (ch) => Math.round(Number(ch) * 10);

/**
 * Whether a trace reaching chMin..chMax runs past the log. Judged on the
 * 0.1 m the Outputs table prints, so the alert and ChMin/ChMax always agree.
 */
export const runsPastLog = (chMin, chMax, startrd, endrd) =>
  tenths(chMin) < tenths(startrd) || tenths(chMax) > tenths(endrd);

/** runsPastLog for a trace drawn between canvas x `left` and `right`. */
export const drawnPastLog = (left, right, { padding, multiplier, startOffsetRD, length }) => {
  const toCh = (x) => (x - padding) / multiplier + startOffsetRD;
  return runsPastLog(toCh(left), toCh(right), startOffsetRD, startOffsetRD + length / multiplier);
};
